import { r as registerInstance, h, f as forceUpdate, H as Host, g as getElement } from './index-ce7d0cf1.js';
import { i as i18nProvider } from './index-5a73d8b4.js';
import { g as getNotificationProvider } from './backend-8895d157.js';
import { a as isDefined } from './type-check-5fa494e1.js';
import './index-b34238aa.js';
import './debounce-9c93e0a1.js';

const datesAreOnSameDay = (first, second) => first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate();
const datesAreOnSameMonth = (first, second) => first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth();

var NotificationClusterNamesEnum;
(function (NotificationClusterNamesEnum) {
    NotificationClusterNamesEnum["TODAY"] = "today";
    NotificationClusterNamesEnum["YESTERDAY"] = "yesterday";
    NotificationClusterNamesEnum["WEEK"] = "week";
    NotificationClusterNamesEnum["MONTH"] = "month";
    NotificationClusterNamesEnum["OLDER"] = "older";
})(NotificationClusterNamesEnum || (NotificationClusterNamesEnum = {}));
const getNotificationClusterName = (notification) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const notificationDate = new Date(notification.timestamp * 1000);
    if (datesAreOnSameDay(notificationDate, today)) {
        return NotificationClusterNamesEnum.TODAY;
    }
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (datesAreOnSameDay(notificationDate, yesterday)) {
        return NotificationClusterNamesEnum.YESTERDAY;
    }
    if (notificationDate.getTime() > today.getTime() - 7 * 24 * 60 * 60 * 1000) {
        return NotificationClusterNamesEnum.WEEK;
    }
    if (datesAreOnSameMonth(notificationDate, today)) {
        return NotificationClusterNamesEnum.MONTH;
    }
    return NotificationClusterNamesEnum.OLDER;
};
const createNotificationCluster = (notifications) => {
    const cluster = {};
    notifications.forEach((item) => {
        const clusterName = getNotificationClusterName(item);
        if (!Array.isArray(cluster[clusterName])) {
            cluster[clusterName] = [item];
        }
        else {
            cluster[clusterName].push(item);
        }
    });
    return cluster;
};
const getClusterName = (str) => {
    const { t } = i18nProvider;
    const fallback = `${str.slice(0, 1).toUpperCase()}${str.slice(1)}`;
    switch (str) {
        case NotificationClusterNamesEnum.TODAY:
            return t("notifications.date.today", fallback);
        case NotificationClusterNamesEnum.YESTERDAY:
            return t("notifications.date.yesterday", fallback);
        case NotificationClusterNamesEnum.WEEK:
            return t("notifications.date.lastWeek", fallback);
        case NotificationClusterNamesEnum.MONTH:
            return t("notifications.date.lastMonth", fallback);
        case NotificationClusterNamesEnum.OLDER:
        default:
            return t("notifications.date.older", fallback);
    }
};

const appNotificationListCss = ":root,.uic-theme-responsive{--uxc-app-header-height:52px;--uxc-app-header-expandables-y-offset:56px;--uxc-app-header-expandables-min-width:300px;--uxc-app-header-z-index:400;--uxc-app-header-bg-color:var(--uic-color-blue5);--uxc-app-header-separator-color:var(--uic-color-white);--uxc-app-header-launcher-width:68px}@media only screen and (min-width: 1024px){.uic-theme-responsive{--uxc-app-header-height:68px;--uxc-app-header-expandables-y-offset:72px;--uxc-app-header-launcher-width:85px}}:root,.uic-theme-responsive{--uxc-app-toast-notifications-z-index:350}.uxc-app-notification-list{display:block}.uxc-app-notification-list__cluster{display:block}.uxc-app-notification-list__list{display:block;margin:0;padding:0;list-style:none}.uxc-app-notification-list__list-header{padding:15px 10px;position:-webkit-sticky;position:sticky;left:0;right:0;top:-2px;width:auto;background-color:#fff;z-index:1;font-weight:var(--ux-font-weight-bold, bold);border-bottom:1px solid rgb(161, 161, 161)}.uxc-app-notification-list__list-footer{padding:15px 10px;display:-ms-flexbox;display:flex;-ms-flex-align:start;align-items:flex-start;-ms-flex-pack:center;justify-content:center;text-align:center;min-height:100px}.uxc-app-notification-list__list-item{margin:0;padding:10px;list-style:none;border-bottom:1px solid rgb(161, 161, 161)}.uxc-app-notification-list__load-more{display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;-ms-flex-pack:center;justify-content:center;text-align:center}.uxc-app-notification-list .ux-notification-item__headline,.uxc-app-notification-list .ux-notification-item__source{font-weight:var(--ux-font-weight-bold, bold)}.uxc-app-notification-list .ux-notification-item__source{text-transform:var(--ux-label-text-tranform, none)}";
const UxcAppNotificationListStyle0 = appNotificationListCss;

const baseClass = "uxc-app-notification-list";
const AppNotificationList = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.notificationSubscriberId = "";
        this.i18nSubscriberId = "";
        this.renderNotificationItem = (data) => {
            return (h("li", { key: `${data.id}`, class: `${baseClass}__list-item` }, h("ux-notification-item", { source: data.sourceName, headline: data.subject, createdAt: `${data.timestamp}`, dateFormat: this.dateFormat, timeFormat: this.timeFormat, severity: data.severity, type: "Overview" }, h("span", { slot: "message" }, data.message))));
        };
        this.renderError = (err) => {
            const { t } = i18nProvider;
            switch (err.status) {
                case 401:
                    return t("notifications.error.unauthorized", "Unauthorized");
                default:
                    return err.message || t("notifications.error.general");
            }
        };
        this.dateFormat = "YYYY-MM-DD";
        this.timeFormat = "HH:mm:ss";
    }
    connectedCallback() {
        this.notificationSubscriberId = getNotificationProvider().subscribe(() => forceUpdate(this));
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }
    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        getNotificationProvider().unsubscribe(this.notificationSubscriberId);
    }
    render() {
        const { t } = i18nProvider;
        const { notifications, error, pending, loadMore } = getNotificationProvider();
        const hasNotifications = notifications.length > 0;
        const cluster = createNotificationCluster(notifications);
        return (h(Host, { key: '6f1f6088e25f549c8bbd4cbecb0ebc7e3be2aef7', class: {
                [`${baseClass}`]: true
            } }, hasNotifications &&
            Object.entries(cluster).map(([clusterName, notifications]) => (h("div", { key: clusterName, class: `${baseClass}__cluster` }, h("header", { class: `${baseClass}__list-header` }, getClusterName(clusterName)), h("ul", { class: `${baseClass}__list` }, notifications.map(this.renderNotificationItem))))), h("div", { key: 'd1d71ea70e1f8805a378d7f06a29834083c6870d', class: `${baseClass}__list-footer` }, pending && h("div", { key: '0e68e4bc8e693b6639812af22bb9e56eec1a461f', class: "ux-horizontal-loader" }), !pending && hasNotifications && loadMore && (h("div", { key: 'ee0071b6961d64efa3deaa4ecdeb656251e7cd87', class: `${baseClass}__load-more` }, h("ux-button", { key: 'e9cfe7323ff8376fc05b3b71cf2ad0879dde1e69', onClick: loadMore }, t("notifications.button.loadMore")))), !pending && !hasNotifications && !isDefined(error) && (h("div", { key: '62f5a4dbaa58c637b2f766eb37e3176da45636e2', class: `${baseClass}__msg--info` }, t("notifications.info.noNotification", "No notifications"))), !pending && !hasNotifications && isDefined(error) && (h("div", { key: '02ad620fe17ec83bc7aef29db154b04f57890738', class: `${baseClass}__msg--error` }, this.renderError(error))))));
    }
    get element() { return getElement(this); }
};
AppNotificationList.style = UxcAppNotificationListStyle0;

export { AppNotificationList as uxc_app_notification_list };

//# sourceMappingURL=uxc-app-notification-list.entry.js.map